import { getStoreId, setStoreId } from "$/hooks/use-store-information"
import jobQueries from "$/lib/queries/job-queries"
import { toast } from "@repo/ui/hooks/use-toast"
import { QueryClient } from "@tanstack/react-query"
import { redirect } from "@tanstack/react-router"
import { isAxiosError } from "axios"
import { loadTranslator } from "./load-translator"

/**
 * Opening a job link from another store switches to that store when the user
 * has access to it. Without access (or if the job doesn't exist) the user is
 * sent to the calendar instead.
 */
export async function ensureJobStoreSelected(
  queryClient: QueryClient,
  jobId: string,
  preload: boolean,
): Promise<void> {
  // Links preload on hover (defaultPreload: "intent"), e.g. "Tidigare jobb"
  // cards from other stores. Only a real navigation may switch store or toast.
  if (preload) return

  const [access, storeId] = await Promise.all([
    queryClient
      .fetchQuery(jobQueries.storeAccess(jobId))
      .catch((error: Error) => {
        // Any other failure (network, 5xx) goes to the regular error screen
        // rather than claiming the user lacks access.
        if (isNoAccessError(error)) return null
        throw error
      }),
    getStoreId(),
  ])

  if (!access) {
    const t = await loadTranslator(queryClient)
    toast({
      title: t("alfred.shell.storeAccess.noJobAccess"),
      variant: "destructive",
    })
    throw redirect({ to: "/calendar", replace: true })
  }

  if (access.storeId === storeId) return

  // Before removeQueries, which also drops the cached dictionary
  const t = await loadTranslator(queryClient)
  await setStoreId(access.storeId)
  // Same reset as the store switcher: every cached query belongs to the old
  // store, including the store information the sidebar is built from.
  queryClient.removeQueries()
  toast({
    title: t("alfred.shell.storeAccess.switchedStore", {
      storeName: access.storeName,
    }),
  })
}

export const isNoAccessError = (error: Error): boolean => {
  if (!isAxiosError(error)) return false
  const status = error.response?.status
  return status === 403 || status === 404
}
